import React, { useRef, useState } from 'react'
import cn from 'classnames'
import { Trans, useTranslation } from 'react-i18next'
import moment from 'moment'

import { minTotalBudgetFloor } from 'domain/runtimeBudget/floors'
import { useCampaignConfig, useCampaignConfigDispatch } from './CampaignConfigProvider'
import BudgetInputBox from './BudgetInputBox'
import CampaignDateInputs from './CampaignDateInputs'
import PreviewModal from './PreviewModal'
import ErrorModal from './ErrorModal'
import { SecondaryButton } from 'components/Shared/Button'
import { localizeDateObj } from 'utils/helpers'
import {
  SETTINGS_PLATFORMS_ANCHOR,
  permissionErrorProviderName,
  updatedProviderLabel,
} from 'domain/platform/permissionErrorTarget'

export default function CampaignConfig() {
  const { t } = useTranslation()

  const { campaign, task, editMode, form, loading, error, campaignStatus } = useCampaignConfig()
  const {
    toggleEditMode,
    resetCampaignForm,
    getDailyBudgetPreview,
    resetDailyBudgetPreview,
    postBudgetAndRuntime,
    hasPermissionError,
    getPermissionErrorPlatform,
    hasPendingTask,
    hasPendingPublishTask,
    fetchCurrentCampaignConfig,
  } = useCampaignConfigDispatch()
  const permissionErrorProvider = permissionErrorProviderName(getPermissionErrorPlatform())
  const [showErrorModal, setShowErrorModal] = useState(false)
  const budgetInputRef = useRef(null)

  const hasTask = !!task
  const isDisabled = hasTask && ['PENDING'].includes(task.status)
  const isEditing = editMode && !isDisabled
  const isReadonly = !isEditing && !isDisabled
  const isStartDateInPast = !!form.start_date && moment(form.start_date).isBefore(moment(), 'day')

  // Validation logic
  const minBudget = minTotalBudgetFloor({
    minBudget: campaign.min_budget,
    minDailyBudget: campaign.min_daily_budget,
    platforms: campaign.platforms,
    days:
      form.end_date && form.start_date
        ? moment(form.end_date).diff(moment(form.start_date), 'days') + 1
        : 1,
  })
  const isBudgetValid = !!form.total_budget && parseFloat(form.total_budget) >= minBudget
  const fallbackStartDate = form.start_date || campaign.start_date
  const isRuntimeValid =
    !!fallbackStartDate &&
    !!form.end_date &&
    moment(form.end_date).isSameOrAfter(moment(fallbackStartDate), 'day')
  const canSave =
    isEditing &&
    isBudgetValid &&
    isRuntimeValid &&
    (!form.start_date || !isStartDateInPast) &&
    // something changed — form.total_budget becomes a string once the input
    // has been typed into, while campaign.total_budget stays a number, so
    // compare numerically rather than with strict equality.
    (parseFloat(form.total_budget) !== campaign.total_budget ||
      (!!form.start_date && form.start_date !== campaign.start_date) ||
      form.end_date !== campaign.end_date)

  const handleEdit = () => {
    toggleEditMode()
    setTimeout(() => budgetInputRef.current.focus(), 0)
  }
  const handleCancel = () => {
    toggleEditMode()
    resetCampaignForm()
  }

  const handleSave = () => {
    getDailyBudgetPreview(({ success }) => {
      if (!success) {
        setShowErrorModal(true)
      }
    })
  }
  const handleModalConfirm = () => {
    postBudgetAndRuntime(({ success }) => {
      toggleEditMode()
      resetDailyBudgetPreview()
      if (!success) {
        setShowErrorModal(true)
      }
    })
  }
  // Only show ErrorModal if error exists and it was triggered by preview/save
  const shouldShowErrorModal =
    showErrorModal && error && (error.errorSource === 'preview' || error.errorSource === 'save')

  // Retry logic: retry the failed action
  const handleRetry = () => {
    setShowErrorModal(false)
    if (error && error.errorSource === 'preview') {
      handleSave()
    } else if (error && error.errorSource === 'save') {
      handleModalConfirm()
    }
  }

  const btClassNames = cn('cbr-bt bt', {
    'bt-editing': isEditing,
    'bt-disabled': isDisabled,
  })
  const inputClassNames = cn('cbr-input', {
    'input-readonly': isReadonly,
    'input-editing': isEditing,
    'input-disabled': isEditing && isDisabled,
  })

  let statusText = ''
  let showFailureStatus = false
  if (hasTask) {
    const mappedStatus =
      task.mappedStatus || (task.status && task.status.toLowerCase()) || 'pending'
    if (mappedStatus === 'pending') {
      statusText = t('Recommendations.campaignConfig.status.pending')
    } else if (mappedStatus === 'success') {
      statusText = t('Recommendations.campaignConfig.status.success', {
        timestamp: task.datetime ? localizeDateObj(task.datetime, 'de', 'DD.MM.YYYY') : '_',
        provider: updatedProviderLabel(campaignStatus),
      })
    } else if (mappedStatus === 'failure') {
      showFailureStatus = true
    } else {
      statusText = task.status || ''
    }
  } else if (hasPendingPublishTask()) {
    statusText = t('Recommendations.campaignConfig.status.notPublished')
  }

  return (
    <>
      <div className="campaign-budget-and-runtime">
        <div className="campaign-budget-and-runtime_container">
          <div
            className={cn('campaign-budget-and-runtime_container__inputs', {
              readonly: isReadonly,
              editing: isEditing,
              disabled: isDisabled,
            })}
          >
            <BudgetInputBox inputClassNames={inputClassNames} budgetInputRef={budgetInputRef} />
            <CampaignDateInputs inputClassNames={inputClassNames} />
            <div className="campaign-budget-and-runtime_container__buttons">
              {isReadonly && !hasPermissionError() && (
                <button
                  className={btClassNames}
                  onClick={handleEdit}
                  disabled={
                    loading || hasPendingTask() || hasPendingPublishTask() || hasPermissionError()
                  }
                >
                  {t('common.edit')}
                </button>
              )}
              {isEditing && (
                <>
                  <button
                    className={cn('bt-cancel', btClassNames)}
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    className={cn('bt-save', btClassNames)}
                    onClick={handleSave}
                    disabled={loading || !canSave}
                  >
                    {loading ? t('common.loading') : t('common.save')}
                  </button>
                </>
              )}
              {isDisabled && (
                <button className="cbr-bt bt" disabled>
                  {t('common.edit')}
                </button>
              )}
            </div>
          </div>
          {!error && !!statusText && !showFailureStatus && (
            <div className="campaign-budget-and-runtime_container__status">{statusText}</div>
          )}
          {hasTask && showFailureStatus && (
            <div className="campaign-budget-and-runtime_container__status">
              <p className="warning-red mb-0">
                {t(
                  'Recommendations.campaignConfig.status.failure',
                  'Failed to update campaign budget/runtime. Please try again.',
                )}
              </p>
            </div>
          )}
          {error &&
            !error.errorSource &&
            (hasPermissionError() ? (
              <div className="d-flex align-items-start justify-content-between gap-4 mt-4">
                <div>
                  <Trans
                    i18nKey="Recommendations.campaignConfig.error.permissionError.surfaceErrorMsg"
                    values={{ provider: permissionErrorProvider }}
                    components={{
                      a: (
                        <a
                          href={SETTINGS_PLATFORMS_ANCHOR}
                          rel="noopener noreferrer"
                          target="_blank"
                          className="d-inline-block"
                          style={{ color: '#2F80ED', textDecoration: 'underline' }}
                        >
                          asset page
                        </a>
                      ),
                    }}
                  />
                </div>
                <SecondaryButton
                  text={t('common.reload')}
                  onClick={fetchCurrentCampaignConfig}
                  color="navy"
                  disabled={loading}
                />
              </div>
            ) : (
              <div className="d-flex align-items-center justify-content-between mt-4">
                <p className="warning-red mb-0">
                  {t(
                    'Recommendations.campaignConfig.loadError',
                    'Failed to load campaign budget/runtime. Please try again.',
                  )}
                </p>
                <SecondaryButton
                  text={t('common.reload')}
                  onClick={fetchCurrentCampaignConfig}
                  color="navy"
                  disabled={loading}
                />
              </div>
            ))}
        </div>
        <PreviewModal
          isGooglePaused={!!campaignStatus?.google?.hasPaused}
          onConfirm={handleModalConfirm}
        />
        <ErrorModal
          isOpen={shouldShowErrorModal}
          onClose={() => setShowErrorModal(false)}
          onRetry={handleRetry}
        />
      </div>
      <hr className="divider divider--darker w-100 my-4" />
    </>
  )
}

